import { getProductType, ProductEnum } from "../../constants/login";
import { DEFAULT_IMPORT_META, getImportOrderRegistry } from "./orderRegistry";
import {
  ExcelExportSheetConfig,
  ExcelExportWorksheetConfig,
  ExcelImportMeta,
} from "../types";
import { getExcelExportSheets } from "../sheetRegistry";
export type ImportPlanWorksheetEntry = {
  payloadKey: string;
  fileName: string;
  worksheetName: string;
  worksheet: ExcelExportWorksheetConfig;
  sheetConfig: ExcelExportSheetConfig;
  importOrder: number;
  importPhase: string;
  dependsOn: string[];
};

export type ImportPlanPhase = {
  phase: string;
  importOrder: number;
  entries: ImportPlanWorksheetEntry[];
};

export type ImportPlanValidationIssue = {
  worksheetName: string;
  message: string;
};

const resolveImportMeta = (
  worksheet: ExcelExportWorksheetConfig,
  registry: Record<string, ExcelImportMeta>,
): Required<
  Pick<ExcelImportMeta, "importOrder" | "importPhase" | "dependsOn">
> => {
  const fromRegistry = registry[worksheet.worksheetName] ?? DEFAULT_IMPORT_META;
  return {
    importOrder: worksheet.importOrder ?? fromRegistry.importOrder,
    importPhase:
      worksheet.importPhase ?? fromRegistry.importPhase ?? "unconfigured",
    dependsOn: worksheet.dependsOn ?? fromRegistry.dependsOn ?? [],
  };
};

/** Flat list of configured worksheets sorted by importOrder (then name). */
export const getOrderedImportPlan = (
  productId: number,
  sheetConfigs?: ExcelExportSheetConfig[],
): ImportPlanWorksheetEntry[] => {
  const productType = getProductType(productId) as ProductEnum | undefined;
  const sheets = sheetConfigs ?? getExcelExportSheets(productId);
  const registry = getImportOrderRegistry(productType);
  const entries: ImportPlanWorksheetEntry[] = [];

  for (const sheetConfig of sheets) {
    for (const worksheet of sheetConfig.worksheets) {
      const meta = resolveImportMeta(worksheet, registry);
      entries.push({
        payloadKey: sheetConfig.payloadKey,
        fileName: sheetConfig.fileName,
        worksheetName: worksheet.worksheetName,
        worksheet,
        sheetConfig,
        importOrder: meta.importOrder,
        importPhase: meta.importPhase,
        dependsOn: [...meta.dependsOn],
      });
    }
  }

  return entries.sort((a, b) => {
    if (a.importOrder !== b.importOrder) {
      return a.importOrder - b.importOrder;
    }
    return a.worksheetName.localeCompare(b.worksheetName);
  });
};

/** Lowest importOrder among worksheet tab names (999 when unknown). */
export const getMinImportOrderForWorksheetNames = (
  productId: number,
  worksheetNames: string[],
  sheetConfigs?: ExcelExportSheetConfig[],
): number => {
  if (worksheetNames.length === 0) {
    return DEFAULT_IMPORT_META.importOrder;
  }

  const plan = getOrderedImportPlan(productId, sheetConfigs);
  const byName = new Map(
    plan.map((entry) => [entry.worksheetName, entry.importOrder]),
  );

  return Math.min(
    ...worksheetNames.map(
      (name) => byName.get(name) ?? DEFAULT_IMPORT_META.importOrder,
    ),
  );
};

/** Validates dependsOn references and basic ordering constraints. */
export const validateImportPlan = (
  plan: ImportPlanWorksheetEntry[],
): ImportPlanValidationIssue[] => {
  const issues: ImportPlanValidationIssue[] = [];
  const byName = new Map<string, ImportPlanWorksheetEntry[]>();

  for (const entry of plan) {
    const list = byName.get(entry.worksheetName) ?? [];
    list.push(entry);
    byName.set(entry.worksheetName, list);
  }

  for (const entry of plan) {
    for (const dependency of entry.dependsOn) {
      const providers = byName.get(dependency);
      if (!providers?.length) {
        issues.push({
          worksheetName: entry.worksheetName,
          message: `dependsOn "${dependency}" is not in the import plan`,
        });
        continue;
      }

      const maxProviderOrder = Math.max(
        ...providers.map((provider) => provider.importOrder),
      );
      if (maxProviderOrder >= entry.importOrder) {
        issues.push({
          worksheetName: entry.worksheetName,
          message: `importOrder ${entry.importOrder} must be greater than "${dependency}" (${maxProviderOrder})`,
        });
      }
    }
  }

  return issues;
};

/** Groups sorted plan entries into sequential API phases (one request per phase). */
export const groupImportPlanIntoPhases = (
  plan: ImportPlanWorksheetEntry[],
): ImportPlanPhase[] => {
  const phaseMap = new Map<string, ImportPlanWorksheetEntry[]>();

  for (const entry of plan) {
    const list = phaseMap.get(entry.importPhase) ?? [];
    list.push(entry);
    phaseMap.set(entry.importPhase, list);
  }

  return Array.from(phaseMap.entries())
    .map(([phase, entries]: [string, ImportPlanWorksheetEntry[]]) => ({
      phase,
      importOrder: Math.min(
        ...entries.map((entry: ImportPlanWorksheetEntry) => entry.importOrder),
      ),
      entries: entries.sort(
        (a: ImportPlanWorksheetEntry, b: ImportPlanWorksheetEntry) => {
          if (a.importOrder !== b.importOrder) {
            return a.importOrder - b.importOrder;
          }
          return a.worksheetName.localeCompare(b.worksheetName);
        },
      ),
    }))
    .sort((a, b) => {
      if (a.importOrder !== b.importOrder) {
        return a.importOrder - b.importOrder;
      }
      return a.phase.localeCompare(b.phase);
    });
};

export const getOrderedImportPhases = (
  productId: number,
  sheetConfigs?: ExcelExportSheetConfig[],
): ImportPlanPhase[] =>
  groupImportPlanIntoPhases(getOrderedImportPlan(productId, sheetConfigs));
